import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { MsalService } from '@azure/msal-angular';
import {
  AUTO_LOG_CATEGORY_FOR_LIB,
  AutoLog,
  CommonUtils,
  LocalStorageService,
  Logger,
} from '../../common/public-api';
import { isNullOrEmpty } from '../utils';
import { UserInfoService } from './user-info.service';

export const AUTHENTICATION_SERVICE = new InjectionToken<AuthenticationService>(
  'AUTHENTICATION_SERVICE'
);

/**
 * Login process status enum
 *
 * @export
 * @enum {number}
 */
export enum LoginStatus {
  Startup = 'startup',
  AcquireTokenSuccess = 'acquireTokenSuccess',
  LoginSuccess = 'loginSuccess',
  Failure = 'failure',
  Logout = 'logout',
}

/**
 * Authentication service interface
 *
 * @export
 * @interface AuthenticationService
 */
export interface AuthenticationService {
  /**
   * Subject for controlling login process.
   */
  loginStateSubject$: Observable<string>;

  /**
   * Whether it is authenticated or not.
   */
  isAuthenticated: boolean;

  /**
   * Authorization token.
   */
  idToken: string;

  /**
   * Initialization actions.
   *
   * @memberof AuthenticationService
   */
  init(): void;

  /**
   * Login action.
   *
   * @param {string} [mail]
   * @memberof AuthenticationService
   */
  login(mail?: string): void;

  /**
   * Logout action.
   *
   * @memberof AuthenticationService
   */
  logout(): void;

  /**
   * Refresh the id token to keep authentication available.
   *
   * @memberof AuthenticationService
   */
  refreshToken(): void;

  /**
   * Check whether the id token is expired or not.
   *
   * @return {*}  {boolean}
   * @memberof AuthenticationService
   */
  checkIdTokenExpiration(): boolean;
  refreshTokenForNoPermission$(): Observable<any>;
  getLoginStatus(): Promise<any>;
}

/**
 * Authentication service base class
 *
 * @export
 * @abstract
 * @class AuthenticationBaseService
 * @implements {AuthenticationService}
 */
export abstract class AuthenticationBaseService
  implements AuthenticationService
{
  loginStateSubject$ = new BehaviorSubject<LoginStatus>(LoginStatus.Startup);

  constructor(protected _userInfoService: UserInfoService) {}

  abstract isAuthenticated: boolean;

  abstract idToken: string;

  init(): void {
    this.loginStateSubject$ = new BehaviorSubject<LoginStatus>(
      LoginStatus.Startup
    );
  }

  abstract login(mail?: string): void;

  logout(): void {
    this._userInfoService.clean();
    this.loginStateSubject$.next(LoginStatus.Logout);
  }

  abstract refreshToken(): void;

  abstract checkIdTokenExpiration(): boolean;

  abstract refreshTokenForNoPermission$(): Observable<any>;
  abstract getLoginStatus(): Promise<any>;
}

/**
 * Simple form authentication service
 *
 * @export
 * @class FormAuthenticationService
 * @extends {AuthenticationBaseService}
 */
@Injectable()
export class FormAuthenticationService extends AuthenticationBaseService {
  constructor(
    userInfoService: UserInfoService,
    private _logger: Logger,
    private localStorageService: LocalStorageService,
    @Inject(MsalService)
    private _authService: MsalService
  ) {
    super(userInfoService);
  }

  get isAuthenticated(): boolean {
    return (
      CommonUtils.isNullOrUndefined(this._userInfoService.currentUser) === false
    );
  }

  get idToken(): string {
    return this.localStorageService.getItem('idToken') || '';
  }

  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  login(mail?: string): void {
    if (CommonUtils.isNullOrUndefined(mail)) {
      throw 'Argument(mail) is empty.';
    }
    const tmpMail = mail as string;
    this._userInfoService.updateUserInfo(tmpMail).subscribe({
      next: (result) => {
        const act = this._authService.instance.getActiveAccount();
        console.log('getActiveAccount', act);
        this.loginStateSubject$.next(LoginStatus.LoginSuccess);
        if (isNullOrEmpty(result)) {
          this.loginStateSubject$.next(LoginStatus.Failure);
        }
      },
      error: (err) => {
        this.loginStateSubject$.next(LoginStatus.Failure);
      },
    });
  }
  /**
   * 儲存登入 Token
   *
   * @param {string} mail 登入者 Email
   * @memberof AuthenticationService
   */
  setJwtToken(mail: string): void {
    // Convert to jwt token.
    const header = {
      typ: 'JWT',
      alg: 'RS256',
      kid: 'nOo3ZDrODXEK1jKWhXslHR_KXEg',
    };

    const payload = {
      aud: 'c64e7d0c-5c25-4132-8e08-999999999999',
      iss: 'https://login.microsoftonline.com/489c2518-e323-45de-8d9e-999999999999/v2.0',
      iat: Math.floor(Date.now() / 1000) - 30,
      nbf: Math.floor(Date.now() / 1000) - 30,
      exp: Math.floor(Date.now() / 1000) + 60 * 60,
      // nonce: '78ec4fc1-e894-4cfc-9b68-037c248b23b7',
      // oid: 'd80c2ccd-cc55-48ea-9e03-be07bce02cc0',
      preferred_username: mail,
      // rh: '0.AQ8AGCWcSCPj3kWNnt-xCt1pbgx9TsYlXDJBjggxf30WJC8PABI.',
      sub: 'kcOrBDOAskdvKkNeQ1aNeqpfAuuzfed9999999999-k',
      // tid: '489c2518-e323-45de-8d9e-dfb10add696e',
      // uti: 'BX3IIXXR8kuu3x2lt9Z6AQ',
      // ver: '2.0'
    };
    const content =
      btoa(encodeURIComponent(JSON.stringify(header))) +
      '.' +
      btoa(encodeURIComponent(JSON.stringify(payload)));
    const signature = btoa(
      encodeURIComponent(JSON.stringify('[mockSignature]'))
    );

    const idToken = content + '.' + signature;

    // set to localStorage
    this.localStorageService.setItem('idToken', idToken);
  }
  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  refreshToken(): void {}

  @AutoLog(AUTO_LOG_CATEGORY_FOR_LIB)
  checkIdTokenExpiration(): boolean {
    return false;
  }
  refreshTokenForNoPermission$(): Observable<any> {
    return of(null);
  }
  public async getLoginStatus(): Promise<any> {
    return null;
  }
}
